import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { computeScores, scoreMeta, priorityActions, computeSipNeeded, computeLumpsumNeeded, computeSipAtRate, SCENARIOS } from '../lib/scoring';
import { fmtINR, fmtDate, scoreColor, scoreBand } from '../lib/format';
import { DISCLAIMERS, PRODUCT_CATEGORIES } from '../lib/disclaimers';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Download, FileText, Share2, Printer } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import MRIReportDocument from '../components/MRIReportDocument';
import { AktaLogo } from '../components/AktaLogo';
import ConversationLog from '../components/ConversationLog';
import SignedReportUpload from '../components/SignedReportUpload';
import api from '../lib/api';
import { ROLES } from '../mock';
import { toast } from '../hooks/use-toast';

const SectionCard = ({ title, sub, children }) => (
  <Card className="border-brand-border">
    <CardContent className="p-5">
      <div className="mb-3">
        <div className="text-sm font-semibold text-brand-indigo">{title}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
      </div>
      {children}
    </CardContent>
  </Card>
);

const ReportPreviewPage = () => {
  const { clientId, sessionId } = useParams();
  const nav = useNavigate();
  const { getClient, getSession, user } = useApp();

  const client = getClient(clientId);
  const session = getSession(sessionId);

  const [draftDownloads, setDraftDownloads] = useState({ count: 0, events: [] });
  useEffect(() => {
    if (!session?.id) return;
    api.listDraftDownloads(session.id).then(setDraftDownloads).catch(() => {});
  }, [session?.id]);

  if (!client || !session) {
    return (
      <div className="p-8 text-center">
        <div className="text-lg font-semibold text-brand-indigo">Session not found.</div>
        <Button className="mt-4" onClick={() => nav(`/clients/${clientId}`)}>Back</Button>
      </div>
    );
  }

  const scores = computeScores(session);
  const actions = priorityActions(session, scores);
  const c = scoreColor(scores.overall);
  const canEdit = user?.role === ROLES.RM && session.rm_id === user.user_id;
  const scenarioShown = session.scenario_shown || 'both';
  const showConservative = scenarioShown === 'both' || scenarioShown === 'conservative';
  const showIllustrative = scenarioShown === 'both' || scenarioShown === 'illustrative';
  const scenarioNote = scenarioShown === 'conservative'
    ? 'SIP scenario at Conservative 10% p.a. Illustrative only. Not guaranteed. See D-8.'
    : scenarioShown === 'illustrative'
      ? 'SIP scenario at AMFI Illustrative 12.5% p.a. Illustrative only. Not guaranteed. See D-8.'
      : 'SIP scenarios at Conservative (10%) and AMFI Illustrative (12.5%) p.a. Capped at 12.5% \u2014 the AMFI-permitted illustrative rate. Not guaranteed. See D-8.';

  const logDownload = () => {
    // Fire-and-forget audit log — compliance trail for DRAFT downloads.
    api.auditDraftDownload(session.id)
      .then(() => api.listDraftDownloads(session.id).then(setDraftDownloads))
      .catch(() => {});
  };

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto space-y-5">
      <div className="flex items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(`/clients/${clientId}/mri/${sessionId}`)} className="text-xs text-brand-indigo font-semibold flex items-center gap-1 hover:underline">
            <ArrowLeft className="h-3 w-3" /> Back to session
          </button>
          {(session.status !== 'completed' && session.status !== 'signed') && (
            <span data-testid="draft-badge" className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-700">
              Draft · watermark will appear on PDF
            </span>
          )}
          {draftDownloads.count > 0 && (
            <span
              data-testid="draft-downloads-chip"
              title={draftDownloads.events.map((e) => `${e.actor_name || e.actor_id || ''} · ${new Date(e.ts).toLocaleString('en-IN')}`).join('\n')}
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border border-brand-indigo/40 bg-brand-bg text-brand-indigo"
            >
              {draftDownloads.count} draft {draftDownloads.count === 1 ? 'download' : 'downloads'} logged
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <PDFDownloadLink
            document={<MRIReportDocument client={client} session={session} scores={scores} rm={user} />}
            fileName={`Akta_Financial_Awareness_${client.name.replace(/\s+/g, '_')}_${session.session_date}.pdf`}
          >
            {({ loading }) => (
              <Button
                data-testid="download-pdf-btn"
                className="bg-brand-green hover:bg-brand-greenDark text-white"
                disabled={loading}
                onClick={logDownload}
              >
                <Download className="h-4 w-4 mr-2" /> {loading ? 'Preparing\u2026' : 'Download PDF'}
              </Button>
            )}
          </PDFDownloadLink>
          <Button variant="outline" onClick={() => { navigator.clipboard.writeText(window.location.href); toast({ title: 'Report link copied' }); }} className="border-brand-border">
            <Share2 className="h-4 w-4 mr-2" /> Share
          </Button>
        </div>
      </div>

      {/* Signed report & conversation log */}
      <div className="grid lg:grid-cols-2 gap-5 no-print">
        <SignedReportUpload session={session} canEdit={canEdit} />
        <ConversationLog session={session} canEdit={canEdit} />
      </div>

      {/* Report Preview */}
      <div id="report-preview" className={`bg-white border border-brand-border rounded-xl overflow-hidden shadow-sm relative ${session.status !== 'completed' && session.status !== 'signed' ? 'preview-draft' : ''}`}>
        {(session.status !== 'completed' && session.status !== 'signed') && (
          <div
            aria-hidden="true"
            data-testid="draft-watermark"
            className="pointer-events-none absolute inset-0 flex items-start justify-center overflow-hidden z-0 select-none"
            style={{ paddingTop: '30%' }}
          >
            <div className="text-[180px] font-black tracking-[0.25em] text-slate-500/25 -rotate-[24deg] whitespace-nowrap">DRAFT</div>
          </div>
        )}
        <div className="relative z-10">
        {/* Branded header */}
        <div className="flex items-center gap-4 p-6 text-white brand-stripe-left" style={{ background: 'linear-gradient(90deg, #1F135A 0%, #2E1F7A 100%)' }}>
          <div className="h-14 w-14 rounded bg-white p-1 flex items-center justify-center">
            <img src="/assets/akta-logo.png" className="h-12 object-contain" alt="Akta Wealth" />
          </div>
          <div className="flex-1">
            <div className="text-xl font-bold">Akta Wealth</div>
            <div className="text-sm opacity-90 mt-1">Client Financial Awareness Report</div>
            <div className="text-[11px] opacity-70 mt-1">AMFI Registered Mutual Fund Distributor · ARN-47282</div>
          </div>
          <div className="text-right text-xs">
            <div className="font-bold text-sm">ARN-47282</div>
            <div className="opacity-85">Not a SEBI Registered Investment Adviser</div>
          </div>
        </div>

        {/* Client bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-6 py-4 bg-brand-bg border-b border-brand-border">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Client</div>
            <div className="font-bold text-brand-indigo">{client.name}</div>
            <div className="text-xs text-muted-foreground">{client.city} · PAN ••••{client.pan_last4}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Session Date</div>
            <div className="font-bold text-brand-indigo">{fmtDate(session.session_date)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Relationship Manager</div>
            <div className="font-bold text-brand-indigo">{user?.name}</div>
            <div className="text-xs text-muted-foreground">EUIN {user?.euin}</div>
          </div>
        </div>

        {/* Overall score */}
        <div className="px-6 py-6 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-1 rounded-lg border border-brand-border p-6 text-center bg-white">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Overall MRI Score</div>
            <div className={`text-6xl font-bold mt-2 ${c.text}`}>{scores.overall}</div>
            <div className="text-xs text-muted-foreground">out of 100 · {scoreBand(scores.overall)}</div>
            <div className="text-[10px] text-muted-foreground mt-3 leading-snug">This score is an awareness indicator based on client-provided data. It is not an investment rating or financial health certificate.</div>
          </div>
          <div className="md:col-span-2 rounded-lg border border-brand-border p-6 bg-white">
            <div className="text-sm font-semibold text-brand-indigo mb-3">Health Band</div>
            <div className="flex gap-1">
              {['Critical', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'].map((b, i) => {
                const active = scoreBand(scores.overall) === b;
                const colors = ['bg-red-500', 'bg-red-400', 'bg-amber-400', 'bg-amber-500', 'bg-emerald-400', 'bg-emerald-500'];
                return (
                  <div key={b} className={`flex-1 rounded-md py-2 text-center text-xs font-semibold ${active ? `${colors[i]} text-white` : 'bg-brand-bg text-muted-foreground'}`}>{b}</div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Risk Profile</span><span className="font-semibold text-brand-indigo">{scores.riskLabel}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Net Worth</span><span className="font-semibold text-brand-indigo">{fmtINR(scores.netWorthValue, { compact: true })}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Monthly Surplus</span><span className="font-semibold text-brand-indigo">{fmtINR(scores.monthlySurplus)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Emergency Cover</span><span className="font-semibold text-brand-indigo">{scores.efMonths.toFixed(1)} months</span></div>
            </div>
          </div>
        </div>

        {/* Diagnostic summary */}
        <div className="px-6 pb-6">
          <div className="text-sm font-semibold text-brand-indigo mb-3">9-Metric Diagnostic Summary</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {scoreMeta.map((m) => {
              const v = scores[m.key];
              const cc = scoreColor(v);
              return (
                <div key={m.key} className="border border-brand-border rounded-lg p-4 bg-white">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{m.label}</div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <div className={`text-2xl font-bold ${cc.text}`}>{v}</div>
                    <div className="text-xs text-muted-foreground">/ 100</div>
                  </div>
                  <div className={`text-xs font-semibold ${cc.text}`}>{scoreBand(v)}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Priority Actions */}
        <div className="px-6 pb-6">
          <SectionCard title="Priority Action Plan" sub="Top actions auto-generated based on gap analysis.">
            {actions.length === 0 ? (
              <div className="text-sm text-emerald-600 font-semibold">All areas look aligned to benchmarks. Continue quarterly reviews.</div>
            ) : (
              <div className="space-y-3">
                {actions.map((a, i) => (
                  <div key={i} className="flex gap-3 border border-brand-border rounded-md p-3 bg-brand-bg/50">
                    <div className="h-7 w-7 rounded-full bg-brand-indigo text-white text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-brand-indigo">{a.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">{a.why}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px]">
                        <span>Timeline: <span className="font-semibold text-brand-indigo">{a.timeline}</span></span>
                        <span>Category: <span className="font-semibold text-brand-indigo">{a.category}</span></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        {/* Goal table */}
        <div className="px-6 pb-6">
          <SectionCard title="Goal Awareness Checklist" sub="Not a financial plan. See disclaimer D-2.">
            {(session.goals || []).length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-brand-indigo bg-brand-bg">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Goal</th>
                      <th className="px-3 py-2 font-semibold text-right">Target</th>
                      <th className="px-3 py-2 font-semibold text-right">Yrs</th>
                      {showConservative && <th className="px-3 py-2 font-semibold text-right text-amber-700">SIP @10%</th>}
                      {showIllustrative && <th className="px-3 py-2 font-semibold text-right">SIP @12.5%</th>}
                      <th className="px-3 py-2 font-semibold text-right">Lumpsum today</th>
                      <th className="px-3 py-2 font-semibold text-right">Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(session.goals || []).map((g, i) => {
                      const lump = computeLumpsumNeeded(g);
                      return (
                        <tr key={i} className="border-b border-brand-border/60">
                          <td className="px-3 py-2 font-medium">{g.type}</td>
                          <td className="px-3 py-2 text-right">{fmtINR(g.amount, { compact: true })}</td>
                          <td className="px-3 py-2 text-right">{g.years}</td>
                          {showConservative && <td className="px-3 py-2 text-right text-amber-700">{fmtINR(computeSipAtRate(g, 0.10))}</td>}
                          {showIllustrative && <td className="px-3 py-2 text-right font-semibold">{fmtINR(computeSipNeeded(g))}</td>}
                          <td className="px-3 py-2 text-right font-semibold">{fmtINR(lump, { compact: true })}</td>
                          <td className="px-3 py-2 text-right">{g.priority}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="text-[10px] text-muted-foreground mt-2">{scenarioNote}</div>
              </div>
            ) : <div className="text-sm text-muted-foreground">No goals recorded.</div>}
          </SectionCard>
        </div>

        {/* Product categories */}
        <div className="px-6 pb-6">
          <SectionCard title="Product Category Awareness" sub="Categories only. No specific scheme names. See disclaimer D-5.">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-brand-indigo bg-brand-bg">
                  <tr><th className="px-3 py-2 font-semibold">Category</th><th className="px-3 py-2 font-semibold">Typical Purpose</th><th className="px-3 py-2 font-semibold">Horizon</th></tr>
                </thead>
                <tbody>
                  {PRODUCT_CATEGORIES.map((p) => (
                    <tr key={p.category} className="border-b border-brand-border/60">
                      <td className="px-3 py-2 font-semibold text-brand-indigo">{p.category}</td>
                      <td className="px-3 py-2">{p.purpose}</td>
                      <td className="px-3 py-2">{p.horizon}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>

        {/* Disclaimers */}
        <div className="px-6 pb-8">
          <SectionCard title="Mandatory Disclaimers (D-1 to D-12)">
            <div className="space-y-2">
              {DISCLAIMERS.map((d) => (
                <div key={d.id} className="border border-brand-border rounded-md p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs font-bold text-brand-indigo">{d.id} — {d.title}</div>
                    <Badge variant="outline" className={d.level === 'HIGH' ? 'text-red-700 border-red-300 bg-red-50' : d.level === 'MEDIUM' ? 'text-amber-700 border-amber-300 bg-amber-50' : 'text-muted-foreground'}>{d.level}</Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground leading-relaxed">{d.text}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* Signatures */}
        <div className="px-6 pb-8 grid md:grid-cols-2 gap-6">
          <div className="border-t-2 border-brand-indigo pt-2">
            <div className="text-xs font-bold text-brand-indigo">Client Signature</div>
            <div className="text-sm text-muted-foreground mt-1">{client.name}</div>
            <div className="text-xs text-muted-foreground">Date: __________________</div>
          </div>
          <div className="border-t-2 border-brand-indigo pt-2">
            <div className="text-xs font-bold text-brand-indigo">Authorised Signatory</div>
            <div className="text-sm text-muted-foreground mt-1">{user?.name} · EUIN {user?.euin}</div>
            <div className="text-xs text-muted-foreground">Akta Wealth · ARN-47282</div>
          </div>
        </div>
        </div>
      </div>

      <div className="text-center text-[11px] text-brand-indigo/60 no-print">
        This is an on-screen preview. Download the branded PDF via the button above for AMFI-compliant hand-off.
      </div>
    </div>
  );
};

export default ReportPreviewPage;
